import { Injectable, NgZone } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, take, tap } from 'rxjs';

import { AvatarApiService } from './avatar-api.service';
import { UserSettingStore } from './user-setting-store.base';
import { UserSettingsService } from './user-settings.service';

/**
 * Cross-tab channel discriminator for avatar changes. This is NOT a /v2/userSettings key — the
 * avatar lives on its own endpoint and its own column. It exists only so sibling tabs sharing the
 * 'user-settings' BroadcastChannel can tell an avatar message apart from a date/number one.
 */
export const AVATAR_BROADCAST_KEY = 'profile.avatar';

/**
 * The current user's avatar, cached for the whole app. Extends UserSettingStore for the per-identity
 * load gating and the shared cross-tab channel; the write path is its own, because the avatar is
 * stored on the user row rather than in the settings blob.
 *
 * Writes are deliberately NOT optimistic, unlike the display-preference stores: the crop dialog
 * shows a spinner and must surface the server's 400 inline, so the caller subscribes to the returned
 * observable and the cached value advances only once the write confirms.
 */
@Injectable({ providedIn: 'root' })
export class AvatarService extends UserSettingStore {
  private avatar = new BehaviorSubject<string | null>(null);

  /** The current user's avatar data URL, or null when none is set. */
  readonly avatar$ = this.avatar.asObservable();

  // The identity (userId) the currently-applied avatar state belongs to, or null while logged
  // out. Assigned from onIdentityPresent() — on the IDENTITY rather than on a ready session, see
  // that override — and captured by load()/setAvatar()/clearAvatar() when the request STARTS, so
  // each applies its result only if it is STILL the same identity when the request CONFIRMS.
  //
  // A plain "bumped on logout" counter is not enough: UserSettingStore.init() only calls
  // onLoggedOut() when userId goes falsy, but a CROSS-TAB identity switch A -> B never does —
  // the shared refresh cookie means isAuthenticated (and userId) never drops to false, it just
  // changes value (see init()'s own comments, and auth-sync.service.ts dispatching APPLY_SESSION
  // with B's auth directly). Tracking the identity itself, instead of a counter tied to logout,
  // catches that case too because ANY change of active identity — not just a logout — moves it:
  //  - write path: an in-flight setAvatar/clearAvatar started as A that confirms after this tab
  //    has become B is dropped instead of applying A's image into B's session and broadcasting
  //    A's face to every sibling tab (all now B).
  //  - load path: a GET issued as A that resolves after the switch to B — or merely out of order
  //    with B's own GET — is dropped instead of overwriting B's avatar with A's.
  //  - broadcast path: a cross-tab message is ignored while activeIdentity is null (logged out),
  //    so a stray broadcast can't repaint an avatar nobody is looking at as.
  // A dropped result is not an error: the caller's subscription still completes normally, so a
  // spinner bound to the returned observable still stops.
  private activeIdentity: number | null = null;

  constructor(
    private api: AvatarApiService,
    userSettings: UserSettingsService,
    store: Store,
    zone: NgZone
  ) {
    super(userSettings, store, zone);
  }

  setAvatar(dataUrl: string): Observable<void> {
    const identity = this.activeIdentity;
    return this.api.set(dataUrl).pipe(
      take(1),
      tap(() => {
        if (identity === this.activeIdentity) this.apply(dataUrl);
      })
    );
  }

  clearAvatar(): Observable<void> {
    const identity = this.activeIdentity;
    return this.api.clear().pipe(
      take(1),
      tap(() => {
        if (identity === this.activeIdentity) this.apply(null);
      })
    );
  }

  /**
   * A different identity is taking over. A cross-tab A -> B switch never passes through logout (the
   * shared refresh cookie keeps isAuthenticated true — see the activeIdentity note above and
   * onLoggedOut), so without this A's face would sit on screen until B's GET returns. Drop it now;
   * load() repaints once B's own GET resolves. The equality guard keeps the first login and a
   * same-identity re-entry a no-op (the avatar is already null there).
   *
   * IT HANGS OFF `onIdentityPresent` AND NOT `load()`, and that is the whole reason the hook
   * exists. `load()` runs only for a session `UserSettingStore` considers READY —
   * `isAuthenticated && onApp && !enrollmentRequired` — so an incoming user whose token is
   * forced-2FA-enrollment-scoped never reaches it: `AuthSyncService.leaveAfterSwitch(true)` sends
   * the tab to `ENROLL_ROUTE`, readiness stays false on both of its remaining counts, and the drop
   * below never ran at all. The OUTGOING operator's face then sat on screen under the incoming one
   * for the whole of their time in the wizard, and for ever if they abandoned it, with no later
   * load to self-heal it — the same shape `AttributePresetStore` closes one store over, on a
   * stronger identifier than a palette. It also moves `activeIdentity` itself onto the identity,
   * which is what makes the write path's guard hold there too: a `setAvatar` started as A and
   * confirming after the switch is dropped instead of broadcasting A's face to every sibling tab.
   *
   * Called on every emission carrying a userId, so it acts only on a CHANGE.
   */
  protected override onIdentityPresent(userId: number): void {
    if (this.activeIdentity !== null && this.activeIdentity !== userId) {
      this.emit(this.avatar, null);
    }
    this.activeIdentity = userId;
  }

  protected load(userId: number): void {
    // No identity bookkeeping here: onIdentityPresent() above owns it, and has already run for this
    // userId in the same emission — see its note for the switch that doing it here cannot see.
    this.api
      .get()
      .pipe(take(1))
      .subscribe({
        next: value => {
          // A stale response for a since-superseded identity (a cross-tab switch, or simply
          // out-of-order with the new identity's own GET) must not overwrite the current avatar.
          if (userId === this.activeIdentity) this.emit(this.avatar, value);
        },
        // Re-arm so the next /app entry retries instead of pinning the session on a blank avatar.
        error: () => this.rearmLoad(userId),
      });
  }

  protected onBroadcast(data: { key?: string; value?: unknown }): void {
    // No active identity (logged out) means nobody is looking at this tab's avatar; ignore.
    if (this.activeIdentity === null) return;
    const value = typeof data?.value === 'string' ? data.value : null;
    this.emit(this.avatar, value);
  }

  protected override handlesKey(key?: string): boolean {
    return key === AVATAR_BROADCAST_KEY;
  }

  /** The avatar lives on its own endpoint/column, not in the /v2/userSettings blob, so a cross-tab
   *  avatar change must NOT invalidate that blob's cache — doing so is a wasted re-fetch. */
  protected override get broadcastInvalidatesSettingsCache(): boolean {
    return false;
  }

  /** The previous user's face must not linger on screen while the next user's load is in flight. */
  protected override onLoggedOut(): void {
    this.activeIdentity = null;
    this.emit(this.avatar, null);
  }

  private apply(value: string | null): void {
    this.emit(this.avatar, value);
    // Tell sibling tabs only after the server confirmed, so a failed write never propagates a
    // value the backend never stored.
    this.broadcast(AVATAR_BROADCAST_KEY, value);
  }
}
